import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircleIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { Card, CardContent } from '../../ui/Card';
import { getServicePositions, getAttendanceHistory } from '../../../services/firebase/meetings';
import type { ServicePosition, MeetingAttendance } from '../../../types/meetings';

interface ServiceCommitmentsCardProps {
  uid: string;
}

export function ServiceCommitmentsCard({ uid }: ServiceCommitmentsCardProps) {
  const navigate = useNavigate();
  const [positions, setPositions] = useState<ServicePosition[]>([]);
  const [attendance, setAttendance] = useState<MeetingAttendance[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const [pos, att] = await Promise.all([
          getServicePositions(uid),
          getAttendanceHistory(uid, 30),
        ]);
        setPositions(pos);
        setAttendance(att);
      } catch {
        // Silently ignore — card simply won't render
      } finally {
        setLoaded(true);
      }
    }
    load();
  }, [uid]);

  const activePositions = useMemo(() => positions.filter(p => p.isActive), [positions]);

  const thisMonthCount = useMemo(() => {
    const now = new Date();
    const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    return attendance.filter(a => a.attendedDate.startsWith(ym)).length;
  }, [attendance]);

  const lastServiceEntry = useMemo(() => {
    return attendance.find(a => a.weeklyRole);
  }, [attendance]);

  // Only render if there's something to show
  if (!loaded) return null;
  if (activePositions.length === 0 && thisMonthCount === 0) return null;

  return (
    <Card variant="default" padding="sm">
      <CardContent>
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center flex-shrink-0">
              <CheckCircleIcon className="w-4 h-4 text-teal-600 dark:text-teal-400" />
            </div>
            <div>
              <h3 className="font-semibold text-surface-900 dark:text-surface-100 text-sm leading-tight">
                Service Work
              </h3>
              <p className="text-xs text-teal-600 dark:text-teal-400 font-medium">Step 12</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate('/meetings')}
            className="flex items-center gap-1 text-xs text-surface-400 hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
          >
            View all
            <ArrowRightIcon className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Active positions */}
        {activePositions.length > 0 && (
          <div className="space-y-1.5 mb-3">
            {activePositions.slice(0, 3).map(position => (
              <div key={position.id} className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-teal-500 flex-shrink-0" />
                <span className="text-sm text-surface-700 dark:text-surface-300 truncate">
                  {position.positionName}
                  {position.meetingName && (
                    <span className="text-surface-400 dark:text-surface-500"> · {position.meetingName}</span>
                  )}
                </span>
              </div>
            ))}
            {activePositions.length > 3 && (
              <p className="text-xs text-surface-400 dark:text-surface-500 pl-3.5">
                +{activePositions.length - 3} more
              </p>
            )}
          </div>
        )}

        {/* Stats row */}
        <div className="flex items-center gap-3 flex-wrap">
          {thisMonthCount > 0 && (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-surface-600 dark:text-surface-400 bg-surface-100 dark:bg-surface-700 px-2.5 py-1 rounded-full">
              {thisMonthCount} meeting{thisMonthCount !== 1 ? 's' : ''} this month
            </span>
          )}
          {lastServiceEntry && (
            <span className="inline-flex items-center gap-1 text-xs text-surface-500 dark:text-surface-400">
              Last served: {lastServiceEntry.weeklyRole}
            </span>
          )}
        </div>

        {/* Recovery framing */}
        <p className="mt-3 text-xs text-surface-400 dark:text-surface-500 italic">
          "Your service work is Step 12 in action."
        </p>
      </CardContent>
    </Card>
  );
}
